/**
 * Shared client-side table sorting.
 *
 * Click-to-sort on column headers is implemented once here and reused
 * by UniTable, PharmaTable, ResourceList, and the remaining list pages.
 * Empty values always sort last, in both directions.
 */
import React from 'react';

const NUMERIC_RE = /^-?\d+(?:\.\d+)?$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

export function isEmptySortValue(value) {
  if (value == null) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

function looksNumeric(value) {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return false;
  const s = String(value).trim();
  return s !== '' && NUMERIC_RE.test(s);
}

function looksDate(value) {
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (typeof value !== 'string') return false;
  const s = value.trim();
  if (!ISO_DATE_RE.test(s)) return false;
  const t = Date.parse(s);
  return Number.isFinite(t);
}

function toTimestamp(value) {
  if (value instanceof Date) return value.getTime();
  return Date.parse(String(value).trim());
}

function toNumber(value) {
  if (typeof value === 'number') return value;
  return Number(String(value).trim());
}

function toText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? '1' : '0';
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (item && typeof item === 'object') {
        return item.full_name || item.name || item.email || item.label || String(item.id ?? '');
      }
      return String(item);
    }).join(', ');
  }
  if (typeof value === 'object') {
    return value.full_name || value.name || value.label || value.email || JSON.stringify(value);
  }
  return String(value);
}

/**
 * Compare two cell values. Empty values are treated as equal here;
 * ``sortRows`` places them last regardless of direction.
 */
export function compareSortValues(a, b) {
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return Number(a) - Number(b);
  }
  if (looksNumeric(a) && looksNumeric(b)) {
    const diff = toNumber(a) - toNumber(b);
    if (diff < 0) return -1;
    if (diff > 0) return 1;
    return 0;
  }
  if (looksDate(a) && looksDate(b)) {
    const diff = toTimestamp(a) - toTimestamp(b);
    if (diff < 0) return -1;
    if (diff > 0) return 1;
    return 0;
  }
  return toText(a).localeCompare(toText(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export function sortRows(rows, { dir = 'asc', getValue } = {}) {
  const list = Array.isArray(rows) ? rows.slice() : [];
  const getter = typeof getValue === 'function' ? getValue : (row) => row;
  const desc = dir === 'desc';
  list.sort((left, right) => {
    const a = getter(left);
    const b = getter(right);
    const aEmpty = isEmptySortValue(a);
    const bEmpty = isEmptySortValue(b);
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    const cmp = compareSortValues(a, b);
    return desc ? -cmp : cmp;
  });
  return list;
}

export function nextSortState(current, key) {
  const curKey = current && current.key != null ? current.key : null;
  const curDir = current && current.dir === 'desc' ? 'desc' : 'asc';
  if (curKey === key) {
    return { key, dir: curDir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: 'asc' };
}

export function parseOrderingParam(ordering) {
  const raw = String(ordering || '').trim();
  if (!raw) return { key: null, dir: 'asc' };
  if (raw.startsWith('-')) return { key: raw.slice(1), dir: 'desc' };
  return { key: raw, dir: 'asc' };
}

export function toOrderingParam(key, dir) {
  if (!key) return '';
  return dir === 'desc' ? `-${key}` : String(key);
}

export function columnIsSortable(column) {
  if (!column) return false;
  if (column.sortable === false) return false;
  if (column.sortable === true) return true;
  const key = column.sortKey || column.key;
  if (key == null || key === '') return false;
  if (key === 'actions' || key === 'open') return false;
  const label = column.label;
  if (label == null || String(label).trim() === '') return false;
  return true;
}

export function columnSortValue(column, row) {
  if (!column) return undefined;
  if (typeof column.sortValue === 'function') return column.sortValue(row);
  const key = column.sortKey || column.key;
  if (key == null || row == null) return undefined;
  return row[key];
}

export function invertSortMap(map) {
  const out = {};
  Object.entries(map || {}).forEach(([col, api]) => {
    if (api != null && out[api] == null) out[api] = col;
  });
  return out;
}

/**
 * Sort the current page of rows. Callers that already refetch with an
 * ``ordering`` query (events / payloads) pass ``onSort`` to UniTable
 * instead of using this hook.
 */
export function useTableSort(rows, options = {}) {
  const {
    columns,
    getValue,
    sortable = true,
    defaultKey = null,
    defaultDir = 'asc',
  } = options;
  const [sort, setSort] = React.useState({ key: defaultKey, dir: defaultDir });

  const sortedRows = React.useMemo(() => {
    if (!sortable || !sort.key || !Array.isArray(rows)) return rows || [];
    return sortRows(rows, {
      dir: sort.dir,
      getValue: (row) => {
        if (typeof getValue === 'function') return getValue(row, sort.key);
        const col = Array.isArray(columns)
          ? columns.find((c) => c.key === sort.key)
          : null;
        if (col) return columnSortValue(col, row);
        return row?.[sort.key];
      },
    });
  }, [rows, sort, sortable, getValue, columns]);

  const toggleSort = React.useCallback((key) => {
    setSort((prev) => nextSortState(prev, key));
  }, []);

  return { sortedRows, sort, toggleSort, setSort };
}
